/**
 * Einsatzplanung — Excel-nahe Ansicht für „Vorschlag RV"
 *
 * Bildet das Excel „Mailing NKL 156 Adress-Einsatzplanung.xlsx" Tab „Vorschlag RV" ab.
 * Block-orientiert: Stamm | Frei | Einsatz | Response | Wandlung | Kennzahlen | Ist.
 * Filter für Block-Gruppen, KWs, Lotterien, Quellen-Zeilen.
 *
 * Planung-Anlage via „+ Neue Planung"-Button. Quellen/KW-Editor folgen in Phase 2.5.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Table, Eye, EyeSlash, MagnifyingGlass, CircleNotch } from '@phosphor-icons/react';
import { getIngestUrl, getApiUrl, authFetch } from '../config';
import SubPageHeader from '../components/SubPageHeader';

interface Planung {
  id: number;
  name: string;
  lottery_id: number | null;
  beschreibung: string | null;
  status: string;
}

interface KwConfig {
  id: number;
  kw_nummer: number;
  jahr: number;
  kw_label: string;
  visible: boolean;
  sort_order: number;
}

interface ResponseHistorieEntry {
  lottery_name: string;
  response_quote: number | null;
  wandlung_quote: number | null;
}

interface Quelle {
  id: number;
  name: string;
  wkz: string | null;
  kategorie: 'eigen' | 'fremd';
  bestand: number;
  anteil: number;
  sort_order: number;
  aktiv: boolean;
  response_historie?: ResponseHistorieEntry[];
}

interface KwDaten {
  quelle_id: number;
  kw_config_id: number;
  frei: number | null;
  einsatz: number | null;
  lose: number | null;
  calls: number | null;
}

interface WkzLotteryStat {
  response_pct: number | null;
  wandlung_pct: number | null;
  gesamt: number;
  einsatz: number;
  lottery_name: string;
}

type BlockKey = 'frei' | 'einsatz' | 'response' | 'wandlung' | 'kennzahlen' | 'ist';

const BLOCK_LABELS: Record<BlockKey, string> = {
  frei: 'Frei',
  einsatz: 'Einsatz',
  response: 'Response',
  wandlung: 'Wandlung',
  kennzahlen: 'Kennzahlen',
  ist: 'Ist (Lose+Calls)'
};

const BLOCK_COLORS: Record<BlockKey, string> = {
  frei: '#fde68a',      // amber 300
  einsatz: '#93c5fd',   // blue 300
  response: '#86efac',  // green 300
  wandlung: '#c4b5fd',  // violet 300
  kennzahlen: '#f9a8d4',// pink 300
  ist: '#fdba74'        // orange 300
};

// Corporate Identity: SKL = ROT, NKL = GRÜN.
function sklNklColor(name: string, active: boolean): { bg: string; border: string; text: string } {
  const isSkl = /^SKL/i.test(name);
  const isNkl = /^NKL/i.test(name);
  if (!active) return { bg: '#f9fafb', border: '#d1d5db', text: '#9ca3af' };
  if (isSkl) return { bg: '#fee2e2', border: '#dc2626', text: '#991b1b' }; // rot
  if (isNkl) return { bg: '#dcfce7', border: '#16a34a', text: '#15803d' }; // grün
  return { bg: '#f3f4f6', border: '#9ca3af', text: '#374151' };
}

function detectSubVariante(name: string): string {
  if (/ mapped alt$/i.test(name)) return 'mapped alt';
  if (/ mapped$/i.test(name)) return 'mapped';
  if (/ Schrader spezial$/i.test(name)) return 'Schrader spezial';
  if (/ Schrader DF$/i.test(name)) return 'Schrader DF';
  if (/ Schrader$/i.test(name)) return 'Schrader';
  if (/ eigen( |$)/i.test(name)) return 'eigen';
  return 'basis';
}

// Zieht den reinen wpool.inf1-Wert aus einem Quellen-Namen.
// Beispiele:
//   "Inaturbo (BOE04216)"        -> "Inaturbo"
//   "Inaturbo mapped (BOE04216)" -> "Inaturbo mapped"
//   "Optin Cb 2019"              -> "Optin Cb 2019"
function extractInf1(name: string): string {
  return (name || '').replace(/\s*\([A-Za-z]+\d+\)\s*$/, '').trim();
}

// Normalisiert Response/Wandlung-Quoten auf Prozent (0..100).
// Datenquellen liefern uneinheitlich: manche als Bruch (0.1786 = 17,86 %),
// manche bereits als Prozent (17.86), manche mehrfach-skaliert (17860 durch
// doppeltes ×100, vgl. Screenshot Issue #39). Response/Wandlung-Quoten liegen
// real unter 100 %. Daher: Bruch (<=1) auf Prozent heben, dann unplausibel
// große Werte schrittweise /10 herunterskalieren bis <=100.
// Beispiele: 0.0467→4,67 | 4.67→4,67 | 178.6→17,86 | 17860→17,86.
function toPct(raw: number | null | undefined): number | null {
  if (raw == null) return null;
  let v = raw <= 1 ? raw * 100 : raw;
  while (v > 100) v = v / 10;
  return v;
}

// Kunden-Testmailing-Liste aus Excel "Vorschlag RV" (Block 3P, NKL156).
// Quelle: `docs/Mailing SKL 159 Adress-Einsatzplanung Testmailings.xlsx`,
// genutzt als Filter um direkt mit dem Kunden-Sheet zu vergleichen.
// Matching erfolgt ueber (hkn, inf1) — inf1 gross-/kleinschreibungs-tolerant.
const RV_LISTE: Array<{ hkn: string; inf1: string }> = [
  { hkn: 'BOE08218', inf1: 'Ina_2024' },
  { hkn: 'BOE08219', inf1: 'Ina_2025' },
  { hkn: 'BOE08056', inf1: 'Inaturbo eigen mapped' },
  { hkn: 'BOE08056', inf1: 'Inaturbo eigen Schrader' },
  { hkn: 'BOE04216', inf1: 'Inaturbo mapped' },
  { hkn: 'BOE04216', inf1: 'Inaturbo Schrader' },
  { hkn: 'BOE01011', inf1: 'Inaturbo Schrader spezial' },
  { hkn: 'BOE01012', inf1: 'Inaturbo Schrader DF' },
  { hkn: 'BOE06216', inf1: 'Optin IB 2021 mapped' },
  { hkn: 'BOE06217', inf1: 'Optin IB 2022 mapped' },
  { hkn: 'BOE06218', inf1: 'Optin IB 2023 mapped' },
  { hkn: 'BOE08106', inf1: 'Optin IB 2024 Schrader' },
  { hkn: 'BOE08051', inf1: 'Optin IB 2025' },
  { hkn: 'BOE08051', inf1: 'Optin IB 2025 mapped' },
  { hkn: 'BOE08051', inf1: 'Optin IB 2025 Schrader' },
  { hkn: 'BOE09168', inf1: 'Optin IB 2026' },
  { hkn: 'BOE08053', inf1: 'Optin KR 2025' },
  { hkn: 'BOE09169', inf1: 'Optin KR 2026' }
];

const RV_LISTE_KEYS = new Set(
  RV_LISTE.map(e => `${e.hkn}|${e.inf1.toLowerCase()}`)
);

function matchesRvListe(hkn: string | null, name: string): boolean {
  if (!hkn) return false;
  const inf1 = extractInf1(name).toLowerCase();
  return RV_LISTE_KEYS.has(`${hkn}|${inf1}`);
}

const NUM_LOTTERIES = 9;

export default function Einsatzplanung() {
  const [planungen, setPlanungen] = useState<Planung[]>([]);
  const [selectedPlanungId, setSelectedPlanungId] = useState<number | null>(null);
  const [kwConfigs, setKwConfigs] = useState<KwConfig[]>([]);
  const [quellen, setQuellen] = useState<Quelle[]>([]);
  const [kwDaten, setKwDaten] = useState<Map<string, KwDaten>>(new Map());
  const [wkzStats, setWkzStats] = useState<Map<string, WkzLotteryStat>>(new Map());
  const [allLotteries, setAllLotteries] = useState<Array<{ id: number; name: string; type: string; startdatum: string }>>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Neue-Planung-Dialog
  const [showNewPlanung, setShowNewPlanung] = useState(false);
  const [newName, setNewName] = useState('');
  const [newLotteryId, setNewLotteryId] = useState<number | ''>('');
  const [submitting, setSubmitting] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // KW-Schnellanlage
  const [newKwInput, setNewKwInput] = useState('');
  const [addingKw, setAddingKw] = useState(false);

  // UI state
  const [visibleBlocks, setVisibleBlocks] = useState<Set<BlockKey>>(
    () => new Set<BlockKey>(['frei', 'einsatz', 'response', 'wandlung', 'kennzahlen', 'ist'])
  );
  const [searchTerm, setSearchTerm] = useState('');
  const [kategorieFilter, setKategorieFilter] = useState<'all' | 'eigen' | 'fremd'>('all');
  const [subVarFilter, setSubVarFilter] = useState<Set<string>>(new Set());
  const [editMode, setEditMode] = useState(false);
  // IDs der explizit ausgeblendeten Quellen (per Checkbox im Edit-Modus)
  const [hiddenQuelleIds, setHiddenQuelleIds] = useState<Set<number>>(new Set());
  // Spalten-Filter: ausgeblendete KWs und Lotterien
  const [hiddenKwIds, setHiddenKwIds] = useState<Set<number>>(new Set());
  const [hiddenLotteries, setHiddenLotteries] = useState<Set<string>>(new Set());
  // Nur Zeilen anzeigen die irgendeinen Einsatz- oder Frei-Wert haben
  const [onlyFilled, setOnlyFilled] = useState(false);
  // Nur Zeilen aus der Kunden-Testmailing-Liste (Excel "Vorschlag RV", Block 3P)
  const [onlyRvListe, setOnlyRvListe] = useState(false);
  // Inline-Edit für Einsatz-Zellen
  const [editCell, setEditCell] = useState<{ quelleId: number; kwId: number } | null>(null);
  const [editValue, setEditValue] = useState<string>('');
  const [savingCell, setSavingCell] = useState<string | null>(null);
  // Dauerhaftes Zeilen-Highlight per Stamm-Zellen-Klick
  const [selectedQuelleId, setSelectedQuelleId] = useState<number | null>(null);
  const toggleSelectRow = (id: number) => setSelectedQuelleId(prev => prev === id ? null : id);
  // Kennzahlen-Sortierung
  const [sortKey, setSortKey] = useState<'rschnitt' | 'wschnitt' | 'produkt' | 'rang' | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const toggleSort = (k: 'rschnitt' | 'wschnitt' | 'produkt' | 'rang') => {
    if (sortKey === k) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(k); setSortDir('desc'); }
  };

  // Benannte gespeicherte Filter (geteilt ueber alle User, pro Planung)
  const [savedFilters, setSavedFilters] = useState<Array<{ id: number; name: string; payload: any }>>([]);
  const [selectedFilterId, setSelectedFilterId] = useState<number | ''>('');

  const saveEinsatz = useCallback(async (quelleId: number, kwId: number, value: number | null) => {
    const key = `${quelleId}-${kwId}`;
    setSavingCell(key);
    try {
      const res = await authFetch(getIngestUrl('/einsatzplanung/daten'), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ quelle_id: quelleId, kw_config_id: kwId, einsatz: value })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setError(err.message || 'Speichern fehlgeschlagen');
        return;
      }
      setKwDaten(prev => {
        const next = new Map(prev);
        const existing = next.get(key);
        next.set(key, {
          quelle_id: quelleId,
          kw_config_id: kwId,
          frei: existing?.frei ?? null,
          einsatz: value,
          lose: existing?.lose ?? null,
          calls: existing?.calls ?? null
        });
        return next;
      });
      setError(null);
    } catch (err) {
      setError('Speichern fehlgeschlagen: ' + (err as Error).message);
    } finally {
      setSavingCell(null);
    }
  }, []);

  const startEdit = (quelleId: number, kwId: number, current: number | null | undefined) => {
    setEditCell({ quelleId, kwId });
    setEditValue(current != null ? String(current) : '');
  };

  const commitEdit = async () => {
    if (!editCell) return;
    const trimmed = editValue.trim();
    const parsed = trimmed === '' ? null : Number(trimmed.replace(',', '.'));
    if (parsed != null && (isNaN(parsed) || parsed < 0)) {
      setEditCell(null);
      return;
    }
    await saveEinsatz(editCell.quelleId, editCell.kwId, parsed);
    setEditCell(null);
  };

  const cancelEdit = () => setEditCell(null);

  const loadPlanungen = useCallback(async () => {
    try {
      const res = await authFetch(getIngestUrl('/einsatzplanung/planungen'));
      if (res.ok) {
        const data = await res.json();
        setPlanungen(data.data || []);
        if (data.data?.length && selectedPlanungId === null) {
          setSelectedPlanungId(data.data[0].id);
        }
      }
    } catch (err) {
      console.error(err);
    }
  }, [selectedPlanungId]);

  const loadAllLotteries = useCallback(async () => {
    try {
      const res = await authFetch(getApiUrl('/lotteries'));
      if (res.ok) {
        const data = await res.json();
        setAllLotteries(Array.isArray(data) ? data : (data.data || []));
      }
    } catch (err) {
      console.error('Lotteries laden fehlgeschlagen:', err);
    }
  }, []);

  const openNewPlanungDialog = () => {
    const today = new Date().toISOString().slice(0, 10);
    const next = [...allLotteries]
      .filter(l => l.startdatum >= today)
      .sort((a, b) => a.startdatum.localeCompare(b.startdatum))[0]
      || [...allLotteries].sort((a, b) => b.startdatum.localeCompare(a.startdatum))[0];
    setNewName(next?.name ?? '');
    setNewLotteryId(next?.id ?? '');
    setCreateError(null);
    setShowNewPlanung(true);
  };

  const clearPlanung = async () => {
    if (selectedPlanungId == null) return;
    const n = quellen.length;
    const kwN = kwConfigs.length;
    if (!window.confirm(`Planung wirklich leeren? Löscht ${n} Quellen + ${kwN} KWs (inkl. aller Zellenwerte). Planung selbst bleibt.`)) return;
    setLoading(true); setError(null);
    try {
      const res = await authFetch(
        getIngestUrl(`/einsatzplanung/planungen/${selectedPlanungId}/clear`),
        { method: 'DELETE' }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setError(err.error || err.message || 'Leeren fehlgeschlagen');
        return;
      }
      await loadPlanungDetails(selectedPlanungId);
    } catch (err) {
      setError('Fehler: ' + (err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const addKw = async () => {
    if (selectedPlanungId == null) return;
    const kw = parseInt(newKwInput, 10);
    if (!kw || kw < 1 || kw > 52) { setError('KW muss zwischen 1 und 52 liegen'); return; }
    setAddingKw(true); setError(null);
    try {
      const res = await authFetch(
        getIngestUrl(`/einsatzplanung/planungen/${selectedPlanungId}/kw`),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ kw_nummer: kw, jahr: new Date().getFullYear() })
        }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setError(err.error || err.message || 'KW anlegen fehlgeschlagen');
        return;
      }
      setNewKwInput('');
      await loadPlanungDetails(selectedPlanungId);
    } catch (err) {
      setError('Fehler: ' + (err as Error).message);
    } finally {
      setAddingKw(false);
    }
  };

  const seedQuellenFromWpool = async () => {
    if (selectedPlanungId == null) return;
    if (!window.confirm('Quellen aus wpool füllen? Legt ~170 Quellen an (1 pro HKN).')) return;
    setLoading(true); setError(null);
    try {
      const res = await authFetch(
        getIngestUrl(`/einsatzplanung/planungen/${selectedPlanungId}/quellen-from-wpool`),
        { method: 'POST' }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setError(err.error || err.message || 'Befüllen fehlgeschlagen');
        return;
      }
      await loadPlanungDetails(selectedPlanungId);
    } catch (err) {
      setError('Fehler: ' + (err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const createPlanung = async () => {
    const name = newName.trim();
    if (!name) { setCreateError('Name erforderlich'); return; }
    setSubmitting(true); setCreateError(null);
    try {
      const res = await authFetch(getIngestUrl('/einsatzplanung/planungen'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, lottery_id: newLotteryId === '' ? null : newLotteryId })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setCreateError(err.error || err.message || 'Anlegen fehlgeschlagen');
        return;
      }
      const data = await res.json();
      const newId: number | undefined = data?.data?.id;
      const listRes = await authFetch(getIngestUrl('/einsatzplanung/planungen'));
      if (listRes.ok) {
        const listData = await listRes.json();
        setPlanungen(listData.data || []);
      }
      if (newId) setSelectedPlanungId(newId);
      setShowNewPlanung(false);
    } catch (err) {
      setCreateError('Fehler: ' + (err as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  const loadPlanungDetails = useCallback(async (planungId: number) => {
    setLoading(true);
    setError(null);
    try {
      const [kwRes, quellenRes, datenRes] = await Promise.all([
        authFetch(getIngestUrl(`/einsatzplanung/planungen/${planungId}/kw`)),
        authFetch(getIngestUrl(`/einsatzplanung/planungen/${planungId}/quellen`)),
        authFetch(getIngestUrl(`/einsatzplanung/planungen/${planungId}/daten`)),
      ]);
      if (kwRes.ok) {
        const data = await kwRes.json();
        setKwConfigs(data.data || []);
      }
      let loadedQuellen: Quelle[] = [];
      if (quellenRes.ok) {
        const data = await quellenRes.json();
        loadedQuellen = data.data || [];
        setQuellen(loadedQuellen);
      }
      if (datenRes.ok) {
        const data = await datenRes.json();
        const map = new Map<string, KwDaten>();
        (data.data || []).forEach((d: KwDaten) => {
          map.set(`${d.quelle_id}-${d.kw_config_id}`, d);
        });
        setKwDaten(map);
      }

      // Multi-Lotterie Response/Wandlung — sendet (wkz, inf1=name) pro Quelle mit,
      // damit Einsatz pro Sub-Variante (hkn, inf1) getrennt ausgeliefert wird.
      const quellenMitWkz = loadedQuellen.filter(q => !!q.wkz);
      const wkzValues = quellenMitWkz.map(q => (q.wkz as string).replace(/^[A-Za-z]+/, ''));
      const inf1Values = quellenMitWkz.map(q => extractInf1(q.name));

      const statsMap = new Map<string, WkzLotteryStat>();

      // Demo-Pfad: Wenn die Planung response_historie pro Quelle mitliefert,
      // diese Werte 1:1 verwenden (Excel-Demo-Daten ohne wpool/cache-Umweg).
      // Cache wird trotzdem gezogen und füllt nur Lücken.
      for (const q of loadedQuellen) {
        if (!q.wkz || !q.response_historie || q.response_historie.length === 0) continue;
        const wkzNum = q.wkz.replace(/^[A-Za-z]+/, '');
        const inf1 = extractInf1(q.name);
        for (const h of q.response_historie) {
          if (!h.lottery_name) continue;
          const key = `${wkzNum}|${inf1}:${h.lottery_name}`;
          statsMap.set(key, {
            response_pct: toPct(h.response_quote),
            wandlung_pct: toPct(h.wandlung_quote),
            gesamt: 0,
            einsatz: 0,
            lottery_name: h.lottery_name,
          });
        }
      }

      if (wkzValues.length > 0) {
        const params = new URLSearchParams();
        params.set('wkz', wkzValues.join(','));
        params.set('inf1', inf1Values.join(','));
        const res = await authFetch(
          getIngestUrl(`/wkz-statistik-cache/response?${params.toString()}`)
        );
        if (res.ok) {
          const data = await res.json();
          for (const row of (data.data || [])) {
            if (!row.lottery_name) continue;
            const inf1 = (row.inf1 || '').trim();
            const key = `${row.wkz}|${inf1}:${row.lottery_name}`;
            // Planungs-Historie hat Vorrang; Cache nur als Fallback wenn Lücke.
            if (statsMap.has(key)) continue;
            statsMap.set(key, {
              response_pct: row.response_pct != null ? toPct(parseFloat(row.response_pct)) : null,
              wandlung_pct: row.wandlung_pct != null ? toPct(parseFloat(row.wandlung_pct)) : null,
              gesamt: parseInt(row.gesamt) || 0,
              einsatz: parseInt(row.einsatz) || 0,
              lottery_name: row.lottery_name
            });
          }
        }
      }
      setWkzStats(statsMap);
    } catch (err) {
      setError('Fehler beim Laden: ' + (err as Error).message);
      // eslint-disable-next-line no-console
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPlanungen();
    loadAllLotteries();
  }, [loadPlanungen, loadAllLotteries]);

  useEffect(() => {
    if (selectedPlanungId !== null) {
      loadPlanungDetails(selectedPlanungId);
    }
  }, [selectedPlanungId, loadPlanungDetails]);

  // Gespeicherte Filter der aktuellen Planung laden
  const loadSavedFilters = useCallback(async () => {
    if (selectedPlanungId == null) {
      setSavedFilters([]);
      setSelectedFilterId('');
      return;
    }
    try {
      const res = await authFetch(getIngestUrl(`/einsatzplanung/planungen/${selectedPlanungId}/filter`));
      const data = await res.json();
      if (res.ok && data.success) {
        setSavedFilters(data.data || []);
      }
    } catch (err) {
      setError('Fehler beim Laden der Filter: ' + (err as Error).message);
    }
  }, [selectedPlanungId]);

  useEffect(() => {
    setSelectedFilterId('');
    loadSavedFilters();
  }, [selectedPlanungId, loadSavedFilters]);

  // Vollen Ansichts-Zustand serialisieren (inkl. ID-basierter Ausblendungen)
  const serializeCurrentView = () => ({
    visibleBlocks: Array.from(visibleBlocks),
    searchTerm,
    kategorieFilter,
    subVarFilter: Array.from(subVarFilter),
    sortKey,
    sortDir,
    hiddenQuelleIds: Array.from(hiddenQuelleIds),
    hiddenKwIds: Array.from(hiddenKwIds),
    hiddenLotteries: Array.from(hiddenLotteries),
  });

  // Gespeicherten Filter anwenden (selectedPlanungId bleibt unberuehrt)
  const applyFilter = (p: any) => {
    if (!p) return;
    setVisibleBlocks(new Set((p.visibleBlocks ?? ['frei', 'einsatz', 'response', 'wandlung', 'kennzahlen', 'ist']) as BlockKey[]));
    setSearchTerm(p.searchTerm ?? '');
    setKategorieFilter(p.kategorieFilter ?? 'all');
    setSubVarFilter(new Set(p.subVarFilter ?? []));
    setSortKey(p.sortKey ?? null);
    setSortDir(p.sortDir ?? 'desc');
    setHiddenQuelleIds(new Set(p.hiddenQuelleIds ?? []));
    setHiddenKwIds(new Set(p.hiddenKwIds ?? []));
    setHiddenLotteries(new Set(p.hiddenLotteries ?? []));
  };

  const saveFilterAs = async () => {
    if (selectedPlanungId == null) return;
    const name = window.prompt('Filter speichern unter:')?.trim();
    if (!name) return;
    try {
      const res = await authFetch(getIngestUrl(`/einsatzplanung/planungen/${selectedPlanungId}/filter`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, payload: serializeCurrentView() })
      });
      if (res.ok) {
        await loadSavedFilters();
      } else {
        const err = await res.json().catch(() => ({}));
        setError(err.message || err.error || 'Speichern fehlgeschlagen');
      }
    } catch (err) {
      setError('Fehler beim Speichern: ' + (err as Error).message);
    }
  };

  const onSelectFilter = (idStr: string) => {
    if (!idStr) {
      setSelectedFilterId('');
      return;
    }
    const id = Number(idStr);
    setSelectedFilterId(id);
    const f = savedFilters.find(x => x.id === id);
    if (f) applyFilter(f.payload);
  };

  const deleteSelectedFilter = async () => {
    if (!selectedFilterId) return;
    if (!window.confirm('Filter löschen?')) return;
    try {
      const res = await authFetch(getIngestUrl(`/einsatzplanung/filter/${selectedFilterId}`), {
        method: 'DELETE'
      });
      if (res.ok) {
        setSelectedFilterId('');
        await loadSavedFilters();
      } else {
        const err = await res.json().catch(() => ({}));
        setError(err.message || err.error || 'Löschen fehlgeschlagen');
      }
    } catch (err) {
      setError('Fehler beim Löschen: ' + (err as Error).message);
    }
  };

  const visibleKws = useMemo(
    () => kwConfigs
      .filter(kw => kw.visible && !hiddenKwIds.has(kw.id))
      .sort((a, b) => a.jahr !== b.jahr ? a.jahr - b.jahr : a.kw_nummer - b.kw_nummer),
    [kwConfigs, hiddenKwIds]
  );

  // Rollierende Berechnung: frei[quelle, kw] = bestand − SUM(einsatz in visibleKws ≤ kw).
  // Quellen ohne Bestand (bestand == null) liefern für alle KW-Zellen null (= leer).
  // Scope: alle quellen × visibleKws (chronologisch, bereits sortiert in visibleKws).
  const freiCalc = useMemo(() => {
    const map = new Map<string, number | null>();
    for (const q of quellen) {
      const bestand = q.bestand;
      if (bestand == null) {
        // Keine Basis → alle KW-Zellen dieser Quelle leer
        for (const kw of visibleKws) {
          map.set(`${q.id}-${kw.id}`, null);
        }
        continue;
      }
      let running = bestand;
      for (const kw of visibleKws) {
        const d = kwDaten.get(`${q.id}-${kw.id}`);
        running -= (d?.einsatz ?? 0);
        map.set(`${q.id}-${kw.id}`, running);
      }
    }
    return map;
  }, [quellen, visibleKws, kwDaten]);

  // Lotterien für die aktuelle Planung: chronologisch (lotteries.startdatum DESC),
  // beginnend bei der Planungs-Lotterie (inkl.), N zurück.
  // Fallback ohne Planungs-Lotterie: die N jüngsten insgesamt.
  const lotteries = useMemo(() => {
    if (allLotteries.length === 0) return [];
    const selectedPlanung = planungen.find(p => p.id === selectedPlanungId);
    const planungsLot = selectedPlanung?.lottery_id != null
      ? allLotteries.find(l => l.id === selectedPlanung.lottery_id)
      : null;
    const cutoff = planungsLot?.startdatum ?? null;
    return [...allLotteries]
      .filter(l => !cutoff || l.startdatum <= cutoff)
      .sort((a, b) => (b.startdatum || '').localeCompare(a.startdatum || ''))
      .slice(0, NUM_LOTTERIES)
      .map(l => l.name);
  }, [allLotteries, planungen, selectedPlanungId]);

  const shownLotteries = useMemo(
    () => lotteries.filter(l => !hiddenLotteries.has(l)),
    [lotteries, hiddenLotteries]
  );

  // Response/Wandlung Lookup — Schluessel mit inf1 (quelle.name), Fallback ohne
  const getStat = useCallback(
    (quelle: Quelle, lotteryName: string): WkzLotteryStat | null => {
      if (!quelle.wkz) return null;
      const numericWkz = quelle.wkz.replace(/^[A-Za-z]+/, '');
      const inf1 = extractInf1(quelle.name);
      return (
        wkzStats.get(`${numericWkz}|${inf1}:${lotteryName}`) ||
        wkzStats.get(`${numericWkz}|:${lotteryName}`) ||
        null
      );
    },
    [wkzStats]
  );

  // Kennzahlen pro Quelle — Excel-Formel:
  //   RSchnitt = MITTELWERT(AO..AS) — 5 jüngste vergangene Lotterien, ohne die Planungs-Lotterie
  //   WSchnitt = MITTELWERT(AX..BB) — analog
  //   Produkt  = RSchnitt × WSchnitt × 100 (Excel rechnet mit Dezimalen; bei uns sind Werte schon in %,
  //              deshalb: rschnitt × wschnitt / 100)
  // Filter beeinflussen die Auswahl: wir nehmen aus den sichtbaren Lotterien die Planungs-Lotterie
  // heraus (= jüngste in der Gesamtliste) und mitteln über die ersten bis zu 5 der restlichen.
  const computeKennzahlen = useCallback(
    (quelle: Quelle) => {
      const planungLottery = lotteries[0]; // Annahme: jüngste Lotterie = Planungs-Lotterie
      const relevant = shownLotteries
        .filter(l => l !== planungLottery)
        .slice(0, 5);

      const responseVals: number[] = [];
      const wandlungVals: number[] = [];
      for (const lot of relevant) {
        const s = getStat(quelle, lot);
        if (s?.response_pct != null) responseVals.push(s.response_pct);
        if (s?.wandlung_pct != null) wandlungVals.push(s.wandlung_pct);
      }
      const rschnitt =
        responseVals.length > 0
          ? responseVals.reduce((a, b) => a + b, 0) / responseVals.length
          : null;
      const wschnitt =
        wandlungVals.length > 0
          ? wandlungVals.reduce((a, b) => a + b, 0) / wandlungVals.length
          : null;
      const produkt =
        rschnitt != null && wschnitt != null ? (rschnitt * wschnitt) / 100 : null;
      return { rschnitt, wschnitt, produkt };
    },
    [shownLotteries, lotteries, getStat]
  );

  // Rang: nach Produkt absteigend
  const rangMap = useMemo(() => {
    const withProdukt = quellen
      .filter(q => q.aktiv)
      .map(q => ({ id: q.id, produkt: computeKennzahlen(q).produkt }))
      .filter(x => x.produkt != null)
      .sort((a, b) => (b.produkt || 0) - (a.produkt || 0));
    const map = new Map<number, number>();
    withProdukt.forEach((x, idx) => map.set(x.id, idx + 1));
    return map;
  }, [quellen, computeKennzahlen]);

  // Filter
  const availableSubVarianten = useMemo(() => {
    const set = new Set<string>();
    quellen.forEach(q => set.add(detectSubVariante(q.name)));
    return Array.from(set).sort();
  }, [quellen]);

  const filteredQuellen = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return quellen
      .filter(q => q.aktiv)
      // Im Edit-Modus alle anzeigen (inkl. ausgeblendete, zum Wieder-Einschalten),
      // außerhalb des Edit-Modus explizit ausgeblendete rausfiltern.
      .filter(q => editMode || !hiddenQuelleIds.has(q.id))
      .filter(q => kategorieFilter === 'all' || q.kategorie === kategorieFilter)
      .filter(q => {
        if (subVarFilter.size === 0) return true;
        return subVarFilter.has(detectSubVariante(q.name));
      })
      .filter(q => {
        if (!term) return true;
        return (
          q.name.toLowerCase().includes(term) ||
          (q.wkz || '').toLowerCase().includes(term)
        );
      })
      .filter(q => !onlyRvListe || matchesRvListe(q.wkz, q.name))
      .filter(q => {
        if (!onlyFilled) return true;
        // Zeile sichtbar wenn irgendwo ein Wert vorhanden ist:
        // Einsatz/Frei (pro KW) oder Response/Wandlung (pro Lotterie)
        for (const kw of kwConfigs) {
          const d = kwDaten.get(`${q.id}-${kw.id}`);
          if (d?.einsatz != null && d.einsatz !== 0) return true;
        }
        if (q.wkz) {
          const numericWkz = q.wkz.replace(/^[A-Za-z]+/, '');
          const inf1 = extractInf1(q.name);
          for (const lot of lotteries) {
            const s =
              wkzStats.get(`${numericWkz}|${inf1}:${lot}`) ||
              wkzStats.get(`${numericWkz}|:${lot}`);
            if (s?.response_pct != null && s.response_pct > 0) return true;
            if (s?.wandlung_pct != null && s.wandlung_pct > 0) return true;
          }
        }
        return false;
      })
      .sort((a, b) => a.sort_order - b.sort_order);
  }, [quellen, searchTerm, kategorieFilter, subVarFilter, editMode, hiddenQuelleIds, onlyFilled, onlyRvListe, kwConfigs, kwDaten, lotteries, wkzStats]);

  // Kennzahlen-Sortierung als separater Schritt (ändert Summen nicht).
  const sortedQuellen = useMemo(() => {
    if (!sortKey) return filteredQuellen;
    const val = (q: Quelle): number | null => {
      if (sortKey === 'rang') return rangMap.get(q.id) ?? null;
      const k = computeKennzahlen(q);
      return sortKey === 'rschnitt' ? k.rschnitt : sortKey === 'wschnitt' ? k.wschnitt : k.produkt;
    };
    const arr = [...filteredQuellen];
    arr.sort((a, b) => {
      const va = val(a), vb = val(b);
      if (va == null && vb == null) return 0;
      if (va == null) return 1;            // nulls ans Ende
      if (vb == null) return -1;
      return sortDir === 'asc' ? va - vb : vb - va;
    });
    return arr;
  }, [filteredQuellen, sortKey, sortDir, rangMap, computeKennzahlen]);

  const toggleBlock = (b: BlockKey) => {
    setVisibleBlocks(prev => {
      const next = new Set(prev);
      if (next.has(b)) next.delete(b);
      else next.add(b);
      return next;
    });
  };

  const toggleSubVar = (v: string) => {
    setSubVarFilter(prev => {
      const next = new Set(prev);
      if (next.has(v)) next.delete(v);
      else next.add(v);
      return next;
    });
  };

  const toggleHidden = (id: number) => {
    setHiddenQuelleIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Sticky-Left Offsets — im Edit-Modus rückt die Checkbox-Spalte davor
  const CW = editMode ? 36 : 0;
  const OFF_QUELLE = CW;
  const OFF_WKZ = CW + 200;
  const OFF_BESTAND = CW + 290;
  const OFF_ANTEIL = CW + 370;
  // Feste Breiten der Stamm-Spalten — MÜSSEN den Offset-Abständen entsprechen,
  // sonst überlappen die sticky-left Spalten beim horizontalen Scrollen
  // (z.B. HKN schiebt sich über Quelle). box-sizing:border-box via stickyLeft.
  const W_QUELLE = 200;   // OFF_WKZ - OFF_QUELLE
  const W_HKN = 90;       // OFF_BESTAND - OFF_WKZ
  const W_BESTAND = 80;   // OFF_ANTEIL - OFF_BESTAND
  const W_ANTEIL = 70;
  const colW = (w: number): React.CSSProperties => ({ width: w, minWidth: w, maxWidth: w });
  const STAMM_COLS = editMode ? 5 : 4;

  // Spalten-Zählung für Summenzeile.
  // Ausgeblendete Blöcke belegen je 1 schmale Platzhalter-Spalte (Wieder-Einblenden).
  const blockCols = {
    frei: visibleBlocks.has('frei') ? visibleKws.length : 1,
    einsatz: visibleBlocks.has('einsatz') ? visibleKws.length : 1,
    response: visibleBlocks.has('response') ? shownLotteries.length : 1,
    wandlung: visibleBlocks.has('wandlung') ? shownLotteries.length : 1,
    kennzahlen: visibleBlocks.has('kennzahlen') ? 4 : 1,
    ist: visibleBlocks.has('ist') ? visibleKws.length * 2 : 1
  };

  // Schmale Platzhalter-Spalte für einen ausgeblendeten Block (Klick blendet ein).
  const blockHeaderStyle: React.CSSProperties = { width: '18px', minWidth: '18px', padding: '2px', cursor: 'pointer', backgroundColor: '#f3f4f6', color: '#9ca3af' };
  const blockPlaceholderCell: React.CSSProperties = { width: '18px', minWidth: '18px', padding: '2px', backgroundColor: '#fafafa' };

  const fmt = (n: number | null | undefined, digits = 0) =>
    n == null ? '' : n.toLocaleString('de-DE', { maximumFractionDigits: digits, minimumFractionDigits: digits });
  const fmtPct = (n: number | null | undefined) =>
    n == null ? '' : `${n.toFixed(2).replace('.', ',')} %`;

  // Sticky-Stile
  const stickyLeft = (left: number, z: number = 10): React.CSSProperties => ({
    position: 'sticky',
    left,
    zIndex: z,
    backgroundColor: 'white',
    boxSizing: 'border-box'
  });
  const stickyTop = (top: number, z: number = 5): React.CSSProperties => ({
    position: 'sticky',
    top,
    zIndex: z
  });

  const cellStyle: React.CSSProperties = {
    padding: '4px 8px',
    border: '1px solid #e5e7eb',
    fontSize: '0.8rem',
    whiteSpace: 'nowrap',
    textAlign: 'right'
  };
  const headerCellStyle: React.CSSProperties = {
    ...cellStyle,
    backgroundColor: '#f3f4f6',
    fontWeight: 600,
    textAlign: 'center'
  };

  // Summen über sichtbare Zeilen — drei Aggregate: Gesamt, Eigen, Fremd
  const summe = useMemo(() => {
    type Agg = {
      count: number;
      bestand: number;
      freiPerKw: Map<number, number>;
      einsatzPerKw: Map<number, number>;
      losePerKw: Map<number, number>;
      callsPerKw: Map<number, number>;
    };
    const empty = (): Agg => ({
      count: 0, bestand: 0,
      freiPerKw: new Map(), einsatzPerKw: new Map(),
      losePerKw: new Map(), callsPerKw: new Map(),
    });
    const gesamt = empty();
    const eigen = empty();
    const fremd = empty();

    const accumulate = (agg: Agg, q: Quelle, kennz: { rschnitt: number | null; wschnitt: number | null }) => {
      agg.count += 1;
      agg.bestand += q.bestand;
      for (const kw of visibleKws) {
        const d = kwDaten.get(`${q.id}-${kw.id}`);
        const freiVal = freiCalc.get(`${q.id}-${kw.id}`);
        if (freiVal != null) agg.freiPerKw.set(kw.id, (agg.freiPerKw.get(kw.id) || 0) + freiVal);
        if (d?.einsatz != null) agg.einsatzPerKw.set(kw.id, (agg.einsatzPerKw.get(kw.id) || 0) + d.einsatz);
        if (d?.einsatz != null && kennz.rschnitt != null) {
          const calls = d.einsatz * kennz.rschnitt / 100;
          agg.callsPerKw.set(kw.id, (agg.callsPerKw.get(kw.id) || 0) + calls);
          if (kennz.wschnitt != null) {
            const lose = calls * kennz.wschnitt / 100;
            agg.losePerKw.set(kw.id, (agg.losePerKw.get(kw.id) || 0) + lose);
          }
        }
      }
    };

    for (const q of filteredQuellen) {
      const kennz = computeKennzahlen(q);
      accumulate(gesamt, q, kennz);
      if (q.kategorie === 'eigen') accumulate(eigen, q, kennz);
      else if (q.kategorie === 'fremd') accumulate(fremd, q, kennz);
    }
    return { gesamt, eigen, fremd };
  }, [filteredQuellen, kwDaten, visibleKws, computeKennzahlen, freiCalc]);

  // Summenzeile — wird oben (sticky) UND unten wiederverwendet.
  // sticky=true: bleibt unter dem Header (top:56) kleben, niedrigerer z-index als Header.
  const sumRow = (
    label: string,
    agg: typeof summe.gesamt,
    bg: string,
    key: string,
    sticky: boolean = false,
  ) => {
    // Weißer Abstandsstreifen unter der fixierten Gesamt-Zeile, damit beim Scrollen
    // die Datenzeilen sauber dahinter verschwinden statt oben angeschnitten zu wirken.
    // Wert (8px) bei Bedarf anpassen.
    const stickyStamm: React.CSSProperties = sticky ? { position: 'sticky', top: 56, zIndex: 14, borderBottom: '8px solid #ffffff' } : {};
    const stickyData: React.CSSProperties = sticky ? { position: 'sticky', top: 56, zIndex: 4, borderBottom: '8px solid #ffffff' } : {};
    return (
      <tr key={key} style={{ backgroundColor: bg, fontWeight: 600 }}>
        {editMode && (
          <td style={{ ...cellStyle, ...stickyLeft(0, 11), backgroundColor: bg, ...stickyStamm }}></td>
        )}
        <td style={{ ...cellStyle, ...stickyLeft(OFF_QUELLE, 11), ...colW(W_QUELLE), overflow: 'hidden', textOverflow: 'ellipsis', backgroundColor: bg, textAlign: 'left', ...stickyStamm }}>
          {label} <span style={{ fontWeight: 400, color: '#6b7280' }}>({agg.count})</span>
        </td>
        <td style={{ ...cellStyle, ...stickyLeft(OFF_WKZ, 11), ...colW(W_HKN), backgroundColor: bg, ...stickyStamm }}></td>
        <td style={{ ...cellStyle, ...stickyLeft(OFF_BESTAND, 11), ...colW(W_BESTAND), backgroundColor: bg, ...stickyStamm }}>
          {fmt(agg.bestand)}
        </td>
        <td style={{ ...cellStyle, ...stickyLeft(OFF_ANTEIL, 11), ...colW(W_ANTEIL), backgroundColor: bg, ...stickyStamm }}></td>
        {visibleBlocks.has('frei') ?
          visibleKws.map(kw => (
            <td key={`${key}-f-${kw.id}`} style={{ ...cellStyle, backgroundColor: bg, ...stickyData }}>
              {fmt(agg.freiPerKw.get(kw.id))}
            </td>
          )) : <td key={`${key}-f-ph`} style={{ ...cellStyle, ...blockPlaceholderCell, ...stickyData }}></td>}
        {visibleBlocks.has('einsatz') ?
          visibleKws.map(kw => (
            <td key={`${key}-e-${kw.id}`} style={{ ...cellStyle, backgroundColor: bg, ...stickyData }}>
              {fmt(agg.einsatzPerKw.get(kw.id))}
            </td>
          )) : <td key={`${key}-e-ph`} style={{ ...cellStyle, ...blockPlaceholderCell, ...stickyData }}></td>}
        {visibleBlocks.has('response') ?
          Array.from({ length: shownLotteries.length }).map((_, i) => (
            <td key={`${key}-r-${i}`} style={{ ...cellStyle, backgroundColor: bg, ...stickyData }}>—</td>
          )) : <td key={`${key}-r-ph`} style={{ ...cellStyle, ...blockPlaceholderCell, ...stickyData }}></td>}
        {visibleBlocks.has('wandlung') ?
          Array.from({ length: shownLotteries.length }).map((_, i) => (
            <td key={`${key}-w-${i}`} style={{ ...cellStyle, backgroundColor: bg, ...stickyData }}>—</td>
          )) : <td key={`${key}-w-ph`} style={{ ...cellStyle, ...blockPlaceholderCell, ...stickyData }}></td>}
        {visibleBlocks.has('kennzahlen') ? (
          <>
            <td style={{ ...cellStyle, backgroundColor: bg, ...stickyData }}>—</td>
            <td style={{ ...cellStyle, backgroundColor: bg, ...stickyData }}>—</td>
            <td style={{ ...cellStyle, backgroundColor: bg, ...stickyData }}>—</td>
            <td style={{ ...cellStyle, backgroundColor: bg, ...stickyData }}>—</td>
          </>
        ) : <td key={`${key}-k-ph`} style={{ ...cellStyle, ...blockPlaceholderCell, ...stickyData }}></td>}
        {visibleBlocks.has('ist') ?
          visibleKws.flatMap(kw => [
            <td key={`${key}-l-${kw.id}`} style={{ ...cellStyle, backgroundColor: bg, ...stickyData }}>
              {fmt(agg.losePerKw.get(kw.id))}
            </td>,
            <td key={`${key}-c-${kw.id}`} style={{ ...cellStyle, backgroundColor: bg, ...stickyData }}>
              {fmt(agg.callsPerKw.get(kw.id))}
            </td>
          ]) : <td key={`${key}-i-ph`} style={{ ...cellStyle, ...blockPlaceholderCell, ...stickyData }}></td>}
      </tr>
    );
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb' }}>
      <SubPageHeader title="Einsatzplanung (Vorschlag RV)" icon={<Table size={24} />} />

      <div style={{ padding: '1rem 2rem', maxWidth: '100%', overflow: 'hidden' }}>
        {/* Planung wählen */}
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap' }}>
          <label style={{ fontWeight: 600, fontSize: '0.9rem' }}>Planung:</label>
          <select
            value={selectedPlanungId ?? ''}
            onChange={e => setSelectedPlanungId(Number(e.target.value))}
            style={{
              padding: '0.4rem 0.6rem',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              fontSize: '0.9rem',
              minWidth: '280px'
            }}
          >
            {planungen.map(p => (
              <option key={p.id} value={p.id}>
                {p.name} ({p.status})
              </option>
            ))}
          </select>
          <button
            onClick={openNewPlanungDialog}
            style={{
              padding: '0.4rem 0.8rem',
              border: '1px solid #3b82f6',
              borderRadius: '4px',
              backgroundColor: '#3b82f6',
              color: '#fff',
              fontSize: '0.85rem',
              fontWeight: 500,
              cursor: 'pointer'
            }}
          >
            + Neue Planung
          </button>
          {selectedPlanungId != null && (quellen.length > 0 || kwConfigs.length > 0) && (
            <button
              onClick={clearPlanung}
              disabled={loading}
              style={{
                padding: '0.4rem 0.8rem',
                border: '1px solid #dc2626',
                borderRadius: '4px',
                backgroundColor: '#fff',
                color: '#dc2626',
                fontSize: '0.85rem',
                fontWeight: 500,
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.6 : 1
              }}
            >
              Planung leeren
            </button>
          )}
          {selectedPlanungId != null && quellen.length === 0 && (
            <button
              onClick={seedQuellenFromWpool}
              disabled={loading}
              style={{
                padding: '0.4rem 0.8rem',
                border: '1px solid #059669',
                borderRadius: '4px',
                backgroundColor: '#059669',
                color: '#fff',
                fontSize: '0.85rem',
                fontWeight: 500,
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.6 : 1
              }}
            >
              Quellen aus wpool füllen
            </button>
          )}
          {selectedPlanungId != null && (
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', marginLeft: '0.5rem' }}>
              <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>KW:</span>
              <input
                type="number"
                min={1}
                max={52}
                value={newKwInput}
                onChange={e => setNewKwInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') addKw(); }}
                placeholder="1–52"
                style={{
                  width: '70px',
                  padding: '0.35rem 0.5rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  fontSize: '0.85rem'
                }}
              />
              <button
                onClick={addKw}
                disabled={addingKw || !newKwInput}
                style={{
                  padding: '0.4rem 0.7rem',
                  border: '1px solid #6b7280',
                  borderRadius: '4px',
                  backgroundColor: '#fff',
                  color: '#111827',
                  fontSize: '0.85rem',
                  fontWeight: 500,
                  cursor: (addingKw || !newKwInput) ? 'not-allowed' : 'pointer',
                  opacity: (addingKw || !newKwInput) ? 0.6 : 1
                }}
              >
                + KW
              </button>
            </div>
          )}
          {selectedPlanungId != null && (
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', marginLeft: '0.5rem' }}>
              <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>Filter:</span>
              <select
                value={selectedFilterId}
                onChange={e => onSelectFilter(e.target.value)}
                style={{
                  padding: '0.35rem 0.5rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  fontSize: '0.85rem',
                  minWidth: '160px'
                }}
              >
                <option value="">— wählen —</option>
                {savedFilters.map(f => (
                  <option key={f.id} value={f.id}>{f.name}</option>
                ))}
              </select>
              <button
                onClick={saveFilterAs}
                disabled={selectedPlanungId == null}
                style={{
                  padding: '0.35rem 0.6rem',
                  border: '1px solid #6b7280',
                  borderRadius: '4px',
                  backgroundColor: '#fff',
                  color: '#111827',
                  fontSize: '0.85rem',
                  fontWeight: 500,
                  cursor: selectedPlanungId == null ? 'not-allowed' : 'pointer',
                  opacity: selectedPlanungId == null ? 0.6 : 1
                }}
              >
                Speichern unter…
              </button>
              <button
                onClick={deleteSelectedFilter}
                disabled={!selectedFilterId}
                style={{
                  padding: '0.35rem 0.6rem',
                  border: '1px solid #dc2626',
                  borderRadius: '4px',
                  backgroundColor: '#fff',
                  color: '#dc2626',
                  fontSize: '0.85rem',
                  fontWeight: 500,
                  cursor: !selectedFilterId ? 'not-allowed' : 'pointer',
                  opacity: !selectedFilterId ? 0.6 : 1
                }}
              >
                Löschen
              </button>
            </div>
          )}
          {loading && <CircleNotch size={16} style={{ animation: 'spin 1s linear infinite' }} />}
          {error && <span style={{ color: '#dc2626', fontSize: '0.85rem' }}>{error}</span>}
        </div>

        {/* Block-Toggles */}
        <div style={{ marginBottom: '0.75rem' }}>
          <span style={{ fontSize: '0.8rem', color: '#6b7280', marginRight: '0.5rem' }}>Spalten-Blöcke:</span>
          {(Object.keys(BLOCK_LABELS) as BlockKey[]).map(b => {
            const active = visibleBlocks.has(b);
            return (
              <button
                key={b}
                onClick={() => toggleBlock(b)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.35rem',
                  padding: '0.3rem 0.7rem',
                  margin: '0 0.3rem 0.3rem 0',
                  border: `1px solid ${active ? '#3b82f6' : '#d1d5db'}`,
                  borderRadius: '999px',
                  backgroundColor: active ? BLOCK_COLORS[b] : '#f9fafb',
                  color: active ? '#111827' : '#9ca3af',
                  cursor: 'pointer',
                  fontSize: '0.8rem',
                  fontWeight: active ? 600 : 400
                }}
              >
                {active ? <Eye size={14} /> : <EyeSlash size={14} />}
                {BLOCK_LABELS[b]}
              </button>
            );
          })}
        </div>

        {/* KW-Filter (wirkt auf Frei/Einsatz/Ist) */}
        {(visibleBlocks.has('frei') || visibleBlocks.has('einsatz') || visibleBlocks.has('ist')) && kwConfigs.length > 0 && (
          <div style={{ marginBottom: '0.5rem', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.3rem' }}>
            <span style={{ fontSize: '0.8rem', color: '#6b7280', marginRight: '0.3rem', minWidth: '80px' }}>KW-Filter:</span>
            <button
              onClick={() => setHiddenKwIds(new Set())}
              style={{ padding: '0.25rem 0.6rem', border: '1px solid #d1d5db', borderRadius: '3px', backgroundColor: '#fff', fontSize: '0.75rem', cursor: 'pointer' }}
            >
              Alle
            </button>
            <button
              onClick={() => setHiddenKwIds(new Set(kwConfigs.filter(kw => kw.visible).map(kw => kw.id)))}
              style={{ padding: '0.25rem 0.6rem', border: '1px solid #d1d5db', borderRadius: '3px', backgroundColor: '#fff', fontSize: '0.75rem', cursor: 'pointer' }}
            >
              Keine
            </button>
            <button
              onClick={() => {
                setHiddenKwIds(prev => {
                  const next = new Set<number>();
                  for (const kw of kwConfigs) {
                    if (!kw.visible) continue;
                    if (!prev.has(kw.id)) next.add(kw.id);
                  }
                  return next;
                });
              }}
              style={{ padding: '0.25rem 0.6rem', border: '1px solid #d1d5db', borderRadius: '3px', backgroundColor: '#fff', fontSize: '0.75rem', cursor: 'pointer' }}
              title="Ausgewählte KWs abwählen, abgewählte auswählen"
            >
              Invertieren
            </button>
            <div style={{ width: '1px', height: '18px', backgroundColor: '#d1d5db', margin: '0 0.2rem' }} />
            {kwConfigs
              .filter(kw => kw.visible)
              .sort((a, b) => a.jahr !== b.jahr ? a.jahr - b.jahr : a.kw_nummer - b.kw_nummer)
              .map(kw => {
                const hidden = hiddenKwIds.has(kw.id);
                return (
                  <button
                    key={kw.id}
                    onClick={() => {
                      setHiddenKwIds(prev => {
                        const next = new Set(prev);
                        if (next.has(kw.id)) next.delete(kw.id);
                        else next.add(kw.id);
                        return next;
                      });
                    }}
                    style={{
                      padding: '0.2rem 0.55rem',
                      border: `1px solid ${hidden ? '#d1d5db' : '#3b82f6'}`,
                      borderRadius: '3px',
                      backgroundColor: hidden ? '#f9fafb' : '#dbeafe',
                      color: hidden ? '#9ca3af' : '#1e40af',
                      fontSize: '0.75rem',
                      cursor: 'pointer',
                      fontWeight: hidden ? 400 : 600
                    }}
                  >
                    {kw.kw_label}
                  </button>
                );
              })}
            <span style={{ marginLeft: 'auto', fontSize: '0.75rem', color: '#6b7280' }}>
              {visibleKws.length}/{kwConfigs.filter(kw => kw.visible).length}
            </span>
          </div>
        )}

        {/* Lotterie-Filter (wirkt auf Response/Wandlung/Kennzahlen) */}
        {(visibleBlocks.has('response') || visibleBlocks.has('wandlung') || visibleBlocks.has('kennzahlen')) && lotteries.length > 0 && (
          <div style={{ marginBottom: '0.5rem', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.3rem' }}>
            <span style={{ fontSize: '0.8rem', color: '#6b7280', marginRight: '0.3rem', minWidth: '80px' }}>Lotterie:</span>
            <button
              onClick={() => setHiddenLotteries(new Set())}
              style={{ padding: '0.25rem 0.6rem', border: '1px solid #d1d5db', borderRadius: '3px', backgroundColor: '#fff', fontSize: '0.75rem', cursor: 'pointer' }}
            >
              Alle
            </button>
            <button
              onClick={() => setHiddenLotteries(new Set(lotteries.filter(l => !/^SKL/i.test(l))))}
              style={{ padding: '0.25rem 0.6rem', border: '1px solid #d1d5db', borderRadius: '3px', backgroundColor: '#fff', fontSize: '0.75rem', cursor: 'pointer' }}
              title="Nur SKL-Lotterien anzeigen"
            >
              Nur SKL
            </button>
            <button
              onClick={() => setHiddenLotteries(new Set(lotteries.filter(l => !/^NKL/i.test(l))))}
              style={{ padding: '0.25rem 0.6rem', border: '1px solid #d1d5db', borderRadius: '3px', backgroundColor: '#fff', fontSize: '0.75rem', cursor: 'pointer' }}
              title="Nur NKL-Lotterien anzeigen"
            >
              Nur NKL
            </button>
            <button
              onClick={() => setHiddenLotteries(new Set(lotteries))}
              style={{ padding: '0.25rem 0.6rem', border: '1px solid #d1d5db', borderRadius: '3px', backgroundColor: '#fff', fontSize: '0.75rem', cursor: 'pointer' }}
            >
              Keine
            </button>
            <button
              onClick={() => {
                setHiddenLotteries(prev => {
                  const next = new Set<string>();
                  for (const l of lotteries) {
                    if (!prev.has(l)) next.add(l);
                  }
                  return next;
                });
              }}
              style={{ padding: '0.25rem 0.6rem', border: '1px solid #d1d5db', borderRadius: '3px', backgroundColor: '#fff', fontSize: '0.75rem', cursor: 'pointer' }}
              title="Auswahl umdrehen"
            >
              Invertieren
            </button>
            <div style={{ width: '1px', height: '18px', backgroundColor: '#d1d5db', margin: '0 0.2rem' }} />
            {lotteries.map(lot => {
              const hidden = hiddenLotteries.has(lot);
              const ci = sklNklColor(lot, !hidden);
              const activeColor = ci.bg;
              const activeBorder = ci.border;
              const activeText = ci.text;
              return (
                <button
                  key={lot}
                  onClick={() => {
                    setHiddenLotteries(prev => {
                      const next = new Set(prev);
                      if (next.has(lot)) next.delete(lot);
                      else next.add(lot);
                      return next;
                    });
                  }}
                  style={{
                    padding: '0.2rem 0.55rem',
                    border: `1px solid ${hidden ? '#d1d5db' : activeBorder}`,
                    borderRadius: '3px',
                    backgroundColor: hidden ? '#f9fafb' : activeColor,
                    color: hidden ? '#9ca3af' : activeText,
                    fontSize: '0.75rem',
                    cursor: 'pointer',
                    fontWeight: hidden ? 400 : 600
                  }}
                >
                  {lot}
                </button>
              );
            })}
            <span style={{ marginLeft: 'auto', fontSize: '0.75rem', color: '#6b7280' }}>
              {shownLotteries.length}/{lotteries.length}
            </span>
          </div>
        )}

        {/* Zeilen-Filter */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'center', marginBottom: '0.75rem' }}>
          <div style={{ position: 'relative' }}>
            <MagnifyingGlass
              size={14}
              style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }}
            />
            <input
              type="text"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder="Name / WKZ…"
              style={{
                padding: '0.35rem 0.5rem 0.35rem 1.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                fontSize: '0.85rem',
                width: '200px'
              }}
            />
          </div>

          <div>
            {(['all', 'eigen', 'fremd'] as const).map(k => (
              <button
                key={k}
                onClick={() => setKategorieFilter(k)}
                style={{
                  padding: '0.3rem 0.7rem',
                  marginRight: '0.3rem',
                  border: `1px solid ${kategorieFilter === k ? '#3b82f6' : '#d1d5db'}`,
                  borderRadius: '999px',
                  backgroundColor: kategorieFilter === k ? '#dbeafe' : '#f9fafb',
                  color: kategorieFilter === k ? '#1e40af' : '#6b7280',
                  fontSize: '0.8rem',
                  cursor: 'pointer',
                  fontWeight: kategorieFilter === k ? 600 : 400
                }}
              >
                {k === 'all' ? 'Alle' : k === 'eigen' ? 'Eigen' : 'Fremd'}
              </button>
            ))}
          </div>

          <button
            onClick={() => setOnlyFilled(v => !v)}
            style={{
              padding: '0.3rem 0.7rem',
              border: `1px solid ${onlyFilled ? '#16a34a' : '#d1d5db'}`,
              borderRadius: '999px',
              backgroundColor: onlyFilled ? '#dcfce7' : '#f9fafb',
              color: onlyFilled ? '#15803d' : '#6b7280',
              fontSize: '0.8rem',
              cursor: 'pointer',
              fontWeight: onlyFilled ? 600 : 400
            }}
            title="Nur Zeilen mit Einsatz- oder Frei-Wert in irgendeiner KW"
          >
            {onlyFilled ? '✓ Nur befüllte' : 'Nur befüllte'}
          </button>

          <button
            onClick={() => setOnlyRvListe(v => !v)}
            style={{
              padding: '0.3rem 0.7rem',
              border: `1px solid ${onlyRvListe ? '#7c3aed' : '#d1d5db'}`,
              borderRadius: '999px',
              backgroundColor: onlyRvListe ? '#ede9fe' : '#f9fafb',
              color: onlyRvListe ? '#6d28d9' : '#6b7280',
              fontSize: '0.8rem',
              cursor: 'pointer',
              fontWeight: onlyRvListe ? 600 : 400
            }}
            title={`Nur Zeilen aus der Kunden-Testmailing-Liste (Excel "Vorschlag RV", ${RV_LISTE.length} Eintraege)`}
          >
            {onlyRvListe ? `✓ Nur RV-Liste (${RV_LISTE.length})` : `Nur RV-Liste (${RV_LISTE.length})`}
          </button>

          {availableSubVarianten.length > 0 && (
            <div>
              <span style={{ fontSize: '0.8rem', color: '#6b7280', marginRight: '0.3rem' }}>Sub-Varianten:</span>
              {availableSubVarianten.map(v => {
                const active = subVarFilter.has(v);
                return (
                  <button
                    key={v}
                    onClick={() => toggleSubVar(v)}
                    style={{
                      padding: '0.25rem 0.6rem',
                      marginRight: '0.25rem',
                      border: `1px solid ${active ? '#3b82f6' : '#d1d5db'}`,
                      borderRadius: '999px',
                      backgroundColor: active ? '#dbeafe' : '#f9fafb',
                      color: active ? '#1e40af' : '#6b7280',
                      fontSize: '0.75rem',
                      cursor: 'pointer',
                      fontWeight: active ? 600 : 400
                    }}
                  >
                    {v}
                  </button>
                );
              })}
              {subVarFilter.size > 0 && (
                <button
                  onClick={() => setSubVarFilter(new Set())}
                  style={{
                    padding: '0.25rem 0.6rem',
                    marginLeft: '0.3rem',
                    border: '1px solid #e5e7eb',
                    borderRadius: '999px',
                    backgroundColor: '#fff',
                    color: '#6b7280',
                    fontSize: '0.75rem',
                    cursor: 'pointer'
                  }}
                >
                  ×
                </button>
              )}
            </div>
          )}

          <button
            onClick={() => setEditMode(m => !m)}
            style={{
              padding: '0.35rem 0.8rem',
              border: `1px solid ${editMode ? '#16a34a' : '#d1d5db'}`,
              borderRadius: '4px',
              backgroundColor: editMode ? '#dcfce7' : '#fff',
              color: editMode ? '#15803d' : '#374151',
              fontSize: '0.8rem',
              fontWeight: editMode ? 600 : 400,
              cursor: 'pointer'
            }}
            title="Im Edit-Modus erscheinen Checkboxen pro Zeile. Abgewählte Zeilen werden nach Verlassen des Modus ausgeblendet."
          >
            {editMode ? '✓ Bearbeiten (aktiv)' : 'Zeilen bearbeiten'}
          </button>

          {hiddenQuelleIds.size > 0 && !editMode && (
            <button
              onClick={() => setHiddenQuelleIds(new Set())}
              style={{
                padding: '0.3rem 0.6rem',
                border: '1px solid #e5e7eb',
                borderRadius: '4px',
                backgroundColor: '#fff',
                color: '#6b7280',
                fontSize: '0.75rem',
                cursor: 'pointer'
              }}
              title="Alle ausgeblendeten Zeilen wieder einblenden"
            >
              {hiddenQuelleIds.size} ausgeblendet · Reset
            </button>
          )}

          <div style={{ marginLeft: 'auto', fontSize: '0.8rem', color: '#6b7280' }}>
            {filteredQuellen.length} / {quellen.filter(q => q.aktiv).length} Zeilen · {shownLotteries.length}/{lotteries.length} Lotterien · {visibleKws.length}/{kwConfigs.filter(k=>k.visible).length} KWs
          </div>
        </div>

        {editMode && (
          <div style={{
            display: 'flex',
            gap: '0.5rem',
            alignItems: 'center',
            marginBottom: '0.5rem',
            padding: '0.5rem 0.75rem',
            backgroundColor: '#f0fdf4',
            border: '1px solid #86efac',
            borderRadius: '4px',
            fontSize: '0.8rem'
          }}>
            <span style={{ color: '#15803d' }}>
              Edit-Modus: Checkboxen nutzen um Zeilen für die Ansicht aus-/einzublenden.
            </span>
            <button
              onClick={() => {
                // Alle aktuell sichtbaren (unter Filter) ausblenden
                const ids = filteredQuellen.map(q => q.id);
                setHiddenQuelleIds(prev => {
                  const next = new Set(prev);
                  ids.forEach(id => next.add(id));
                  return next;
                });
              }}
              style={{ padding: '0.2rem 0.5rem', border: '1px solid #d1d5db', borderRadius: '3px', backgroundColor: '#fff', fontSize: '0.75rem', cursor: 'pointer' }}
            >
              Alle abwählen
            </button>
            <button
              onClick={() => setHiddenQuelleIds(new Set())}
              style={{ padding: '0.2rem 0.5rem', border: '1px solid #d1d5db', borderRadius: '3px', backgroundColor: '#fff', fontSize: '0.75rem', cursor: 'pointer' }}
            >
              Alle einblenden
            </button>
            <button
              onClick={() => {
                setHiddenQuelleIds(prev => {
                  const next = new Set<number>();
                  for (const q of quellen) {
                    if (!q.aktiv) continue;
                    if (!prev.has(q.id)) next.add(q.id);
                  }
                  return next;
                });
              }}
              style={{ padding: '0.2rem 0.5rem', border: '1px solid #d1d5db', borderRadius: '3px', backgroundColor: '#fff', fontSize: '0.75rem', cursor: 'pointer' }}
              title="Ausgeblendete Zeilen einblenden, sichtbare Zeilen ausblenden"
            >
              Invertieren
            </button>
            <span style={{ marginLeft: 'auto', color: '#6b7280' }}>
              {quellen.filter(q => q.aktiv).length - hiddenQuelleIds.size} sichtbar · {hiddenQuelleIds.size} abgewählt
            </span>
          </div>
        )}

        {/* Tabelle */}
        <div
          style={{
            border: '1px solid #e5e7eb',
            borderRadius: '6px',
            overflow: 'auto',
            maxHeight: 'calc(100vh - 300px)',
            backgroundColor: 'white'
          }}
        >
          <table style={{ borderCollapse: 'collapse', fontSize: '0.8rem' }}>
            <thead>
              {/* Block-Header-Zeile */}
              <tr>
                <th colSpan={STAMM_COLS} style={{ ...headerCellStyle, ...stickyLeft(0, 20), ...stickyTop(0, 20), backgroundColor: '#e5e7eb' }}>
                  Stamm{editMode ? ' (Edit)' : ''}
                </th>
                {visibleBlocks.has('frei') ? (
                  <th colSpan={visibleKws.length} style={{ ...headerCellStyle, ...stickyTop(0, 15), backgroundColor: BLOCK_COLORS.frei }}>
                    Frei ({visibleKws.length} KW)
                  </th>
                ) : (
                  <th rowSpan={2} title="Frei einblenden" onClick={() => toggleBlock('frei')} style={{ ...headerCellStyle, ...stickyTop(0, 15), ...blockHeaderStyle }}>⊕</th>
                )}
                {visibleBlocks.has('einsatz') ? (
                  <th colSpan={visibleKws.length} style={{ ...headerCellStyle, ...stickyTop(0, 15), backgroundColor: BLOCK_COLORS.einsatz }}>
                    Einsatz ({visibleKws.length} KW)
                  </th>
                ) : (
                  <th rowSpan={2} title="Einsatz einblenden" onClick={() => toggleBlock('einsatz')} style={{ ...headerCellStyle, ...stickyTop(0, 15), ...blockHeaderStyle }}>⊕</th>
                )}
                {visibleBlocks.has('response') ? (
                  <th colSpan={shownLotteries.length} style={{ ...headerCellStyle, ...stickyTop(0, 15), backgroundColor: BLOCK_COLORS.response }}>
                    Response ({shownLotteries.length}/{lotteries.length} Lotterien)
                  </th>
                ) : (
                  <th rowSpan={2} title="Response einblenden" onClick={() => toggleBlock('response')} style={{ ...headerCellStyle, ...stickyTop(0, 15), ...blockHeaderStyle }}>⊕</th>
                )}
                {visibleBlocks.has('wandlung') ? (
                  <th colSpan={shownLotteries.length} style={{ ...headerCellStyle, ...stickyTop(0, 15), backgroundColor: BLOCK_COLORS.wandlung }}>
                    Wandlung ({shownLotteries.length}/{lotteries.length} Lotterien)
                  </th>
                ) : (
                  <th rowSpan={2} title="Wandlung einblenden" onClick={() => toggleBlock('wandlung')} style={{ ...headerCellStyle, ...stickyTop(0, 15), ...blockHeaderStyle }}>⊕</th>
                )}
                {visibleBlocks.has('kennzahlen') ? (
                  <th colSpan={4} style={{ ...headerCellStyle, ...stickyTop(0, 15), backgroundColor: BLOCK_COLORS.kennzahlen }}>
                    Kennzahlen
                  </th>
                ) : (
                  <th rowSpan={2} title="Kennzahlen einblenden" onClick={() => toggleBlock('kennzahlen')} style={{ ...headerCellStyle, ...stickyTop(0, 15), ...blockHeaderStyle }}>⊕</th>
                )}
                {visibleBlocks.has('ist') ? (
                  <th colSpan={visibleKws.length * 2} style={{ ...headerCellStyle, ...stickyTop(0, 15), backgroundColor: BLOCK_COLORS.ist }}>
                    Ist — Lose + Calls ({visibleKws.length} KW)
                  </th>
                ) : (
                  <th rowSpan={2} title="Ist einblenden" onClick={() => toggleBlock('ist')} style={{ ...headerCellStyle, ...stickyTop(0, 15), ...blockHeaderStyle }}>⊕</th>
                )}
              </tr>
              {/* Sub-Header-Zeile */}
              <tr>
                {editMode && (
                  <th style={{ ...headerCellStyle, ...stickyLeft(0, 20), ...stickyTop(28, 20), width: '36px', minWidth: '36px', padding: '4px 6px' }}>
                    <input
                      type="checkbox"
                      checked={quellen.filter(q => q.aktiv).every(q => !hiddenQuelleIds.has(q.id))}
                      onChange={e => {
                        if (e.target.checked) setHiddenQuelleIds(new Set());
                        else setHiddenQuelleIds(new Set(quellen.filter(q => q.aktiv).map(q => q.id)));
                      }}
                      title="Alle ein/aus"
                    />
                  </th>
                )}
                <th style={{ ...headerCellStyle, ...stickyLeft(OFF_QUELLE, 20), ...stickyTop(28, 20), ...colW(W_QUELLE), textAlign: 'left' }}>Quelle</th>
                <th style={{ ...headerCellStyle, ...stickyLeft(OFF_WKZ, 20), ...stickyTop(28, 20), ...colW(W_HKN) }}>HKN</th>
                <th style={{ ...headerCellStyle, ...stickyLeft(OFF_BESTAND, 20), ...stickyTop(28, 20), ...colW(W_BESTAND) }}>Bestand</th>
                <th style={{ ...headerCellStyle, ...stickyLeft(OFF_ANTEIL, 20), ...stickyTop(28, 20), ...colW(W_ANTEIL) }}>Anteil</th>
                {visibleBlocks.has('frei') &&
                  visibleKws.map(kw => (
                    <th key={`frei-${kw.id}`} style={{ ...headerCellStyle, ...stickyTop(28, 15), backgroundColor: BLOCK_COLORS.frei }}>
                      {kw.kw_label}
                    </th>
                  ))}
                {visibleBlocks.has('einsatz') &&
                  visibleKws.map(kw => (
                    <th key={`einsatz-${kw.id}`} style={{ ...headerCellStyle, ...stickyTop(28, 15), backgroundColor: BLOCK_COLORS.einsatz }}>
                      {kw.kw_label}
                    </th>
                  ))}
                {visibleBlocks.has('response') &&
                  shownLotteries.map(lot => (
                    <th key={`r-${lot}`} style={{ ...headerCellStyle, ...stickyTop(28, 15), backgroundColor: sklNklColor(lot, true).bg, color: sklNklColor(lot, true).text }}>
                      {lot}
                    </th>
                  ))}
                {visibleBlocks.has('wandlung') &&
                  shownLotteries.map(lot => (
                    <th key={`w-${lot}`} style={{ ...headerCellStyle, ...stickyTop(28, 15), backgroundColor: sklNklColor(lot, true).bg, color: sklNklColor(lot, true).text }}>
                      {lot}
                    </th>
                  ))}
                {visibleBlocks.has('kennzahlen') && (
                  <>
                    <th onClick={() => toggleSort('rschnitt')} title="Sortieren" style={{ ...headerCellStyle, ...stickyTop(28, 15), backgroundColor: BLOCK_COLORS.kennzahlen, cursor: 'pointer' }}>RSchnitt{sortKey === 'rschnitt' ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}</th>
                    <th onClick={() => toggleSort('wschnitt')} title="Sortieren" style={{ ...headerCellStyle, ...stickyTop(28, 15), backgroundColor: BLOCK_COLORS.kennzahlen, cursor: 'pointer' }}>WSchnitt{sortKey === 'wschnitt' ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}</th>
                    <th onClick={() => toggleSort('produkt')} title="Sortieren" style={{ ...headerCellStyle, ...stickyTop(28, 15), backgroundColor: BLOCK_COLORS.kennzahlen, cursor: 'pointer' }}>Produkt{sortKey === 'produkt' ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}</th>
                    <th onClick={() => toggleSort('rang')} title="Sortieren" style={{ ...headerCellStyle, ...stickyTop(28, 15), backgroundColor: BLOCK_COLORS.kennzahlen, cursor: 'pointer' }}>Rang{sortKey === 'rang' ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}</th>
                  </>
                )}
                {visibleBlocks.has('ist') &&
                  visibleKws.flatMap(kw => [
                    <th key={`lose-${kw.id}`} style={{ ...headerCellStyle, ...stickyTop(28, 15), backgroundColor: BLOCK_COLORS.ist, fontSize: '0.7rem' }}>
                      L {kw.kw_label}
                    </th>,
                    <th key={`calls-${kw.id}`} style={{ ...headerCellStyle, ...stickyTop(28, 15), backgroundColor: BLOCK_COLORS.ist, fontSize: '0.7rem' }}>
                      C {kw.kw_label}
                    </th>
                  ])}
              </tr>
            </thead>
            <tbody>
              {/* Obere Gesamt-Summenzeile (sticky unter dem Header) */}
              {filteredQuellen.length > 0 && sumRow('Gesamt', summe.gesamt, '#e5e7eb', 'sum-gesamt-top', true)}
              {sortedQuellen.map(quelle => {
                const kennz = computeKennzahlen(quelle);
                const rang = rangMap.get(quelle.id);
                const subVar = detectSubVariante(quelle.name);
                const isHidden = hiddenQuelleIds.has(quelle.id);
                const isSelected = selectedQuelleId === quelle.id;
                const rowBg = isSelected ? '#fef9c3' : (isHidden ? '#fafafa' : (quelle.kategorie === 'fremd' ? '#fffbeb' : 'white'));
                const rowOpacity = editMode && isHidden ? 0.45 : 1;
                return (
                  <tr key={quelle.id} style={{ opacity: rowOpacity, backgroundColor: isSelected ? '#fefce8' : undefined }}>
                    {editMode && (
                      <td style={{ ...cellStyle, ...stickyLeft(0, 11), backgroundColor: rowBg, textAlign: 'center', padding: '4px 6px' }}>
                        <input
                          type="checkbox"
                          checked={!isHidden}
                          onChange={() => toggleHidden(quelle.id)}
                        />
                      </td>
                    )}
                    <td
                      style={{
                        ...cellStyle,
                        ...stickyLeft(OFF_QUELLE, 11),
                        ...colW(W_QUELLE),
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        textAlign: 'left',
                        backgroundColor: rowBg,
                        cursor: 'pointer'
                      }}
                      title={`${quelle.name}${subVar !== 'basis' ? ` · ${subVar}` : ''}`}
                      onClick={() => toggleSelectRow(quelle.id)}
                    >
                      {quelle.name}
                      {subVar !== 'basis' && (
                        <span
                          style={{
                            display: 'inline-block',
                            marginLeft: '0.4rem',
                            padding: '0 0.35rem',
                            fontSize: '0.65rem',
                            color: '#6b7280',
                            backgroundColor: '#f3f4f6',
                            borderRadius: '3px'
                          }}
                        >
                          {subVar}
                        </span>
                      )}
                    </td>
                    <td style={{ ...cellStyle, ...stickyLeft(OFF_WKZ, 11), ...colW(W_HKN), backgroundColor: rowBg, fontFamily: 'monospace', fontSize: '0.75rem', cursor: 'pointer' }} onClick={() => toggleSelectRow(quelle.id)}>
                      {quelle.wkz || ''}
                    </td>
                    <td style={{ ...cellStyle, ...stickyLeft(OFF_BESTAND, 11), ...colW(W_BESTAND), backgroundColor: rowBg, cursor: 'pointer' }} onClick={() => toggleSelectRow(quelle.id)}>
                      {fmt(quelle.bestand)}
                    </td>
                    <td style={{ ...cellStyle, ...stickyLeft(OFF_ANTEIL, 11), ...colW(W_ANTEIL), backgroundColor: rowBg, cursor: 'pointer' }} onClick={() => toggleSelectRow(quelle.id)}>
                      {quelle.anteil != null ? `${(quelle.anteil * 100).toFixed(2)}%` : ''}
                    </td>
                    {visibleBlocks.has('frei') ?
                      visibleKws.map(kw => (
                        <td key={`f-${kw.id}`} style={cellStyle}>
                          {fmt(freiCalc.get(`${quelle.id}-${kw.id}`))}
                        </td>
                      )) : <td key="f-ph" style={{ ...cellStyle, ...blockPlaceholderCell }}></td>}
                    {visibleBlocks.has('einsatz') ?
                      visibleKws.map(kw => {
                        const d = kwDaten.get(`${quelle.id}-${kw.id}`);
                        const cellKey = `${quelle.id}-${kw.id}`;
                        const isEditing = editCell?.quelleId === quelle.id && editCell?.kwId === kw.id;
                        const isSaving = savingCell === cellKey;
                        const editableStyle: React.CSSProperties = {
                          ...cellStyle,
                          cursor: 'pointer',
                          backgroundColor: isSaving ? '#fef3c7' : (isSelected ? '#fef9c3' : (d?.einsatz != null ? '#eff6ff' : 'white')),
                          padding: isEditing ? 0 : cellStyle.padding
                        };
                        if (isEditing) {
                          return (
                            <td key={`e-${kw.id}`} style={editableStyle}>
                              <input
                                autoFocus
                                type="text"
                                value={editValue}
                                onChange={e => setEditValue(e.target.value)}
                                onBlur={commitEdit}
                                onKeyDown={e => {
                                  if (e.key === 'Enter') { e.preventDefault(); commitEdit(); }
                                  else if (e.key === 'Escape') { e.preventDefault(); cancelEdit(); }
                                }}
                                style={{
                                  width: '100%',
                                  padding: '4px 8px',
                                  border: '2px solid #3b82f6',
                                  borderRadius: 0,
                                  fontSize: '0.8rem',
                                  fontFamily: 'inherit',
                                  textAlign: 'right',
                                  outline: 'none',
                                  boxSizing: 'border-box'
                                }}
                              />
                            </td>
                          );
                        }
                        return (
                          <td
                            key={`e-${kw.id}`}
                            style={editableStyle}
                            title={isSaving ? 'Speichere …' : 'Klicken zum Bearbeiten'}
                            onClick={() => !isSaving && startEdit(quelle.id, kw.id, d?.einsatz)}
                          >
                            {fmt(d?.einsatz)}
                          </td>
                        );
                      }) : <td key="e-ph" style={{ ...cellStyle, ...blockPlaceholderCell }}></td>}
                    {visibleBlocks.has('response') ?
                      shownLotteries.map(lot => {
                        const s = getStat(quelle, lot);
                        return (
                          <td key={`r-${lot}`} style={cellStyle}>
                            {fmtPct(s?.response_pct ?? null)}
                          </td>
                        );
                      }) : <td key="r-ph" style={{ ...cellStyle, ...blockPlaceholderCell }}></td>}
                    {visibleBlocks.has('wandlung') ?
                      shownLotteries.map(lot => {
                        const s = getStat(quelle, lot);
                        return (
                          <td key={`w-${lot}`} style={cellStyle}>
                            {fmtPct(s?.wandlung_pct ?? null)}
                          </td>
                        );
                      }) : <td key="w-ph" style={{ ...cellStyle, ...blockPlaceholderCell }}></td>}
                    {visibleBlocks.has('kennzahlen') ? (
                      <>
                        <td style={cellStyle}>{fmtPct(kennz.rschnitt)}</td>
                        <td style={cellStyle}>{fmtPct(kennz.wschnitt)}</td>
                        <td style={cellStyle}>
                          {kennz.produkt != null ? kennz.produkt.toFixed(3).replace('.', ',') : ''}
                        </td>
                        <td style={cellStyle}>{rang ?? ''}</td>
                      </>
                    ) : <td key="k-ph" style={{ ...cellStyle, ...blockPlaceholderCell }}></td>}
                    {visibleBlocks.has('ist') ?
                      visibleKws.flatMap(kw => {
                        const d = kwDaten.get(`${quelle.id}-${kw.id}`);
                        // Roland-Formel: Calls aus Einsatz × RSchnitt, Lose aus Calls × WSchnitt.
                        // Dialfire-Fehler bleiben so ohne Einfluss auf die Planung.
                        const einsatz = d?.einsatz;
                        const calls = einsatz != null && kennz.rschnitt != null
                          ? einsatz * kennz.rschnitt / 100
                          : null;
                        const lose = calls != null && kennz.wschnitt != null
                          ? calls * kennz.wschnitt / 100
                          : null;
                        return [
                          <td key={`l-${kw.id}`} style={{ ...cellStyle, color: '#6b7280', fontStyle: 'italic' }} title="berechnet: Calls × WSchnitt">
                            {fmt(lose, 1)}
                          </td>,
                          <td key={`c-${kw.id}`} style={{ ...cellStyle, color: '#6b7280', fontStyle: 'italic' }} title="berechnet: Einsatz × RSchnitt">
                            {fmt(calls, 1)}
                          </td>
                        ];
                      }) : <td key="i-ph" style={{ ...cellStyle, ...blockPlaceholderCell }}></td>}
                  </tr>
                );
              })}

              {/* Summenzeilen: Gesamt / Eigen / Fremd — jeweils nur rendern wenn count > 0 */}
              {filteredQuellen.length > 0 && [
                sumRow('Gesamt', summe.gesamt, '#e5e7eb', 'sum-gesamt'),
                summe.eigen.count > 0 && sumRow('Eigen', summe.eigen, '#f3f4f6', 'sum-eigen'),
                summe.fremd.count > 0 && sumRow('Fremd', summe.fremd, '#f3f4f6', 'sum-fremd'),
              ].filter(Boolean)}

              {filteredQuellen.length === 0 && !loading && (
                <tr>
                  <td colSpan={STAMM_COLS + blockCols.frei + blockCols.einsatz + blockCols.response + blockCols.wandlung + blockCols.kennzahlen + blockCols.ist} style={{ ...cellStyle, textAlign: 'center', color: '#9ca3af', padding: '2rem' }}>
                    Keine Zeilen (Filter prüfen)
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div style={{ marginTop: '0.75rem', fontSize: '0.75rem', color: '#6b7280' }}>
          Vergleiche mit Excel „Mailing NKL 156 Adress-Einsatzplanung.xlsx" → Tab „Vorschlag RV". Read-only Prototyp.
        </div>
      </div>

      {showNewPlanung && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
          }}
          onClick={() => !submitting && setShowNewPlanung(false)}
        >
          <div
            style={{
              background: '#fff', borderRadius: '8px', padding: '1.5rem',
              minWidth: '400px', maxWidth: '90vw', boxShadow: '0 10px 25px rgba(0,0,0,0.15)'
            }}
            onClick={e => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.05rem' }}>Neue Planung anlegen</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <label style={{ fontSize: '0.85rem', fontWeight: 500 }}>
                Name
                <input
                  type="text"
                  value={newName}
                  onChange={e => setNewName(e.target.value)}
                  placeholder="z.B. SKL 159"
                  autoFocus
                  style={{
                    marginTop: '0.25rem', display: 'block', width: '100%',
                    padding: '0.45rem 0.6rem', border: '1px solid #d1d5db',
                    borderRadius: '4px', fontSize: '0.9rem', boxSizing: 'border-box'
                  }}
                />
              </label>
              <label style={{ fontSize: '0.85rem', fontWeight: 500 }}>
                Lotterie
                <select
                  value={newLotteryId}
                  onChange={e => setNewLotteryId(e.target.value ? Number(e.target.value) : '')}
                  style={{
                    marginTop: '0.25rem', display: 'block', width: '100%',
                    padding: '0.45rem 0.6rem', border: '1px solid #d1d5db',
                    borderRadius: '4px', fontSize: '0.9rem', boxSizing: 'border-box'
                  }}
                >
                  <option value="">— keine —</option>
                  {[...allLotteries]
                    .sort((a, b) => b.startdatum.localeCompare(a.startdatum))
                    .map(l => (
                      <option key={l.id} value={l.id}>
                        {l.name} — {l.startdatum}
                      </option>
                    ))}
                </select>
              </label>
              {createError && (
                <div style={{ color: '#dc2626', fontSize: '0.85rem' }}>{createError}</div>
              )}
            </div>
            <div style={{ marginTop: '1.25rem', display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
              <button
                onClick={() => setShowNewPlanung(false)}
                disabled={submitting}
                style={{
                  padding: '0.45rem 0.9rem', border: '1px solid #d1d5db',
                  borderRadius: '4px', background: '#fff',
                  cursor: submitting ? 'not-allowed' : 'pointer', fontSize: '0.85rem'
                }}
              >
                Abbrechen
              </button>
              <button
                onClick={createPlanung}
                disabled={submitting || !newName.trim()}
                style={{
                  padding: '0.45rem 0.9rem', border: '1px solid #3b82f6',
                  borderRadius: '4px', background: '#3b82f6', color: '#fff',
                  cursor: (submitting || !newName.trim()) ? 'not-allowed' : 'pointer',
                  fontSize: '0.85rem', fontWeight: 500,
                  opacity: (submitting || !newName.trim()) ? 0.6 : 1
                }}
              >
                {submitting ? 'Speichert…' : 'Anlegen'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
